import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Check, Clock, Receipt, CreditCard, ChevronDown, ChevronRight, Settings, Info } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useQontoSilentSync } from "../hooks/useQontoSilentSync";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { formatCurrency, formatCurrencyFull } from "../utils/formatters";
import { Link } from "react-router-dom";

const COLORS = {
  principale: '#210875',
  accent: '#897FCB',
  bg: '#FDFCFF',
  success: '#059669',
  warning: '#f59e0b',
  error: '#dc2626',
  text: '#210875'
};

// Taux de TVA disponibles
const TAUX_TVA = [
  { value: 20, label: "20% (taux normal)" },
  { value: 10, label: "10% (taux intermédiaire)" },
  { value: 5.5, label: "5.5% (taux réduit)" },
  { value: 2.1, label: "2.1% (taux super réduit)" },
  { value: 0, label: "0% (exonéré)" },
];

// Ventes Page with Client Autocomplete and Monthly Accordion
export const VentesPage = () => {
  const [ventes, setVentes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [statutTva, setStatutTva] = useState(null);
  const [formData, setFormData] = useState({ 
    date: new Date().toISOString().split('T')[0], 
    client: "", 
    description: "", 
    montant: "", 
    taux_tva: 20,
    montant_tva: null,
    montant_ttc: null,
    statut: "payé", 
    activite_type: "principale", 
    deduire_frais: false, 
    date_paiement: new Date().toISOString().split('T')[0] 
  });
  const [actionLoading, setActionLoading] = useState(null);
  const api = useApi();
  const { user } = useAuth();

  useEffect(() => { loadVentes(); }, []);
  // Synchro Qonto silencieuse à l'ouverture (recharge les ventes ~3 sec après si Qonto a livré du neuf)
  useQontoSilentSync(() => loadVentes());

  const loadVentes = async () => {
    try {
      const [ventesRes, dashboardRes] = await Promise.all([
        api.get("/ventes"),
        api.get("/dashboard")
      ]);
      setVentes(ventesRes.data);
      const uniqueClients = [...new Set(ventesRes.data.map(v => v.client))];
      setClients(uniqueClients);
      setStatutTva(dashboardRes.data.statut_tva || null);
    } catch (err) { toast.error("Erreur de chargement"); }
    setLoading(false);
  };

  // Calculer TVA quand montant ou taux change (pour assujettis)
  const calculerTva = (montantHt, tauxTva) => {
    if (!statutTva?.assujetti || !montantHt) {
      return { montant_tva: null, montant_ttc: null };
    }
    const montant = parseFloat(montantHt) || 0;
    const taux = parseFloat(tauxTva) || 0;
    const montantTva = montant * (taux / 100);
    const montantTtc = montant + montantTva;
    return {
      montant_tva: Math.round(montantTva * 100) / 100,
      montant_ttc: Math.round(montantTtc * 100) / 100
    };
  };

  // Handler pour changement de montant HT
  const handleMontantChange = (value) => {
    const tvaData = calculerTva(value, formData.taux_tva);
    setFormData({ ...formData, montant: value, ...tvaData });
  };

  // Handler pour changement de taux TVA
  const handleTauxTvaChange = (value) => {
    const tvaData = calculerTva(formData.montant, value);
    setFormData({ ...formData, taux_tva: parseFloat(value), ...tvaData });
  };

  // Marquer une vente comme payée (encaissée) avec date du jour
  const handleMarquerPaye = async (venteId) => {
    setActionLoading(venteId);
    try {
      await api.post(`/ventes/${venteId}/marquer-paye`);
      toast.success("Vente encaissée ! CA comptabilisé sur ce mois.");
      loadVentes();
    } catch (err) {
      toast.error("Erreur lors de l'encaissement");
    }
    setActionLoading(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Recalculer TVA avant envoi
      const tvaData = calculerTva(formData.montant, formData.taux_tva);
      const submitData = { 
        ...formData, 
        montant: parseFloat(formData.montant),
        // Champs TVA (pour assujettis)
        taux_tva: statutTva?.assujetti ? formData.taux_tva : null,
        montant_tva: statutTva?.assujetti ? tvaData.montant_tva : null,
        montant_ttc: statutTva?.assujetti ? tvaData.montant_ttc : null,
        // Si statut payé, inclure la date de paiement (par défaut = date de vente si non spécifiée)
        date_paiement: formData.statut === "payé" ? (formData.date_paiement || formData.date) : null
      };
      
      if (editingId) {
        await api.put(`/ventes/${editingId}`, submitData);
        toast.success("Vente modifiée");
      } else {
        await api.post("/ventes", submitData);
        toast.success("Vente ajoutée");
        if (formData.deduire_frais && user?.frais_plateforme_pourcent > 0) {
          toast.info(`Frais plateforme (${user.frais_plateforme_pourcent}%) ajoutés aux dépenses`);
        }
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        date: new Date().toISOString().split('T')[0], 
        client: "", 
        description: "", 
        montant: "", 
        taux_tva: 20,
        montant_tva: null,
        montant_ttc: null,
        statut: "payé", 
        activite_type: "principale", 
        deduire_frais: false, 
        date_paiement: new Date().toISOString().split('T')[0] 
      });
      loadVentes();
    } catch (err) { toast.error("Erreur lors de l'enregistrement"); }
  };

  const handleEdit = (vente) => {
    const tvaData = calculerTva(vente.montant, vente.taux_tva || 20);
    setFormData({ 
      date: vente.date, 
      client: vente.client, 
      description: vente.description, 
      montant: vente.montant.toString(), 
      taux_tva: vente.taux_tva || 20,
      montant_tva: tvaData.montant_tva,
      montant_ttc: tvaData.montant_ttc,
      statut: vente.statut, 
      activite_type: vente.activite_type || "principale", 
      deduire_frais: vente.deduire_frais || false,
      date_paiement: vente.date_paiement || vente.date
    });
    setEditingId(vente.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette vente ?")) {
      try { 
        await api.delete(`/ventes/${id}`); 
        toast.success("Vente supprimée"); 
        loadVentes(); 
      } catch (err) { 
        const message = err.response?.data?.detail || "Erreur";
        toast.error(message);
      }
    }
  };

  const filteredClients = clients.filter(c => c.toLowerCase().includes(formData.client.toLowerCase()) && c !== formData.client);
  const currentYear = new Date().getFullYear();
  // Helper local : date d'encaissement (régime micro = comptabilité de caisse)
  // — défini plus bas dans le module avec getDateComptable, on l'inline ici pour ne pas casser l'ordre
  const _dateEncaissement = (v) => (v.statut === "payé" && v.date_paiement ? v.date_paiement : v.date);
  const totalVentes = ventes.filter(v => v.statut === "payé" && _dateEncaissement(v)?.startsWith(currentYear.toString())).reduce((sum, v) => sum + v.montant, 0);
  const enAttente = ventes.filter(v => v.statut === "en attente").reduce((sum, v) => sum + v.montant, 0);

  const fraisPourcent = user?.frais_plateforme_pourcent || 0;
  const now = new Date();
  const moisCourant = now.toISOString().slice(0, 7);
  const moisCourantNum = now.getMonth() + 1;
  const anneeCourante = now.getFullYear();
  
  // Noms des mois pour l'affichage
  const moisNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  // Déterminer la fréquence de déclaration de l'utilisateur
  const frequenceDeclaration = user?.declaration_frequence || "mensuel";
  
  // CA à déclarer = basé sur la date d'encaissement (date_paiement si existe, sinon date de vente)
  const getDateComptable = (vente) => {
    if (vente.statut === "payé" && vente.date_paiement) {
      return vente.date_paiement;
    }
    return vente.date;
  };
  
  // Calcul des mois/périodes à déclarer selon la fréquence
  let periodeADeclarer = "";
  let moisADeclarerList = []; // Liste des mois (format YYYY-MM) qui font partie de la période à déclarer
  let caADeclarer = 0;
  
  if (frequenceDeclaration === "mensuel") {
    // Mode MENSUEL : mois précédent
    const moisPrecedentNum = moisCourantNum === 1 ? 12 : moisCourantNum - 1;
    const anneeMoisPrecedent = moisCourantNum === 1 ? anneeCourante - 1 : anneeCourante;
    const moisPrecedent = `${anneeMoisPrecedent}-${String(moisPrecedentNum).padStart(2, '0')}`;
    
    periodeADeclarer = `${moisNames[moisPrecedentNum - 1]} ${anneeMoisPrecedent}`;
    moisADeclarerList = [moisPrecedent];
    
    caADeclarer = ventes.filter(v => v.statut === "payé" && getDateComptable(v).startsWith(moisPrecedent))
      .reduce((sum, v) => sum + v.montant, 0);
  } else {
    // Mode TRIMESTRIEL : trimestre à déclarer selon le mois courant
    // T1 (jan-fév-mars) → déclaré en avril (deadline 30 avril)
    // T2 (avr-mai-juin) → déclaré en juillet (deadline 31 juillet)
    // T3 (juil-août-sept) → déclaré en octobre (deadline 31 octobre)
    // T4 (oct-nov-déc) → déclaré en janvier N+1 (deadline 31 janvier)
    
    let trimestreNum, anneeTrimestreNominal;
    
    if (moisCourantNum === 1) {
      // Janvier : on déclare T4 de l'année précédente
      trimestreNum = 4;
      anneeTrimestreNominal = anneeCourante - 1;
    } else if (moisCourantNum >= 2 && moisCourantNum <= 4) {
      // Février-Mars-Avril : on prépare/déclare T1
      trimestreNum = 1;
      anneeTrimestreNominal = anneeCourante;
    } else if (moisCourantNum >= 5 && moisCourantNum <= 7) {
      // Mai-Juin-Juillet : on prépare/déclare T2
      trimestreNum = 2;
      anneeTrimestreNominal = anneeCourante;
    } else if (moisCourantNum >= 8 && moisCourantNum <= 10) {
      // Août-Sept-Octobre : on prépare/déclare T3
      trimestreNum = 3;
      anneeTrimestreNominal = anneeCourante;
    } else {
      // Nov-Déc : on prépare T4
      trimestreNum = 4;
      anneeTrimestreNominal = anneeCourante;
    }
    
    // Mois du trimestre
    const trimestres = {
      1: [1, 2, 3],   // T1 : janvier, février, mars
      2: [4, 5, 6],   // T2 : avril, mai, juin
      3: [7, 8, 9],   // T3 : juillet, août, septembre
      4: [10, 11, 12] // T4 : octobre, novembre, décembre
    };
    
    const moisDuTrimestre = trimestres[trimestreNum];
    moisADeclarerList = moisDuTrimestre.map(m => `${anneeTrimestreNominal}-${String(m).padStart(2, '0')}`);
    
    const moisLabels = moisDuTrimestre.map(m => moisNames[m - 1]).join(', ');
    periodeADeclarer = `T${trimestreNum} ${anneeTrimestreNominal} (${moisLabels})`;
    
    // CA du trimestre
    caADeclarer = ventes.filter(v => 
      v.statut === "payé" && 
      moisADeclarerList.some(m => getDateComptable(v).startsWith(m))
    ).reduce((sum, v) => sum + v.montant, 0);
  }
  
  // CA du mois courant (pour info)
  const caADeclarerMoisCourant = ventes.filter(v => v.statut === "payé" && getDateComptable(v).startsWith(moisCourant)).reduce((sum, v) => {
    return sum + v.montant;
  }, 0);

  // Grouper par mois (comme la page dépenses)
  const groupByMonth = (items) => {
    const groups = {};
    const currentMonth = `${anneeCourante}-${String(moisCourantNum).padStart(2, '0')}`;
    
    items.forEach(item => {
      const month = item.date.substring(0, 7);
      if (!groups[month]) groups[month] = [];
      groups[month].push(item);
    });
    
    // Trier les mois du plus récent au plus ancien
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, items]) => ({
        month,
        items: items.sort((a, b) => b.date.localeCompare(a.date)),
        total: items.filter(i => i.statut === "payé").reduce((sum, i) => sum + i.montant, 0),
        isCurrent: month === currentMonth,
        isToDeclarate: moisADeclarerList.includes(month)  // Mois faisant partie de la période à déclarer
      }));
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const renderGroupedVentes = () => {
    const grouped = groupByMonth(ventes);
    
    return (
      <div className="divide-y">
        {grouped.map(({ month, items, total, isCurrent, isToDeclarate }) => (
          <div key={month}>
            {/* Header du mois */}
            <button
              onClick={() => !isToDeclarate && toggleMonth(month)}
              className={`w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors ${isToDeclarate ? 'cursor-default' : 'cursor-pointer'}`}
              style={{ backgroundColor: isToDeclarate ? COLORS.bg : undefined }}
            >
              <div className="flex items-center gap-3">
                {!isToDeclarate && (
                  expandedMonths[month] ? <ChevronDown size={18} /> : <ChevronRight size={18} />
                )}
                <span className="font-semibold" style={{ color: COLORS.text }}>
                  {formatMonth(month)}
                  {isToDeclarate && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.accent + '30', color: COLORS.text }}>À déclarer</span>}
                  {isCurrent && !isToDeclarate && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.principale + '20', color: COLORS.principale }}>Mois en cours</span>}
                </span>
                <span className="text-sm text-muted-foreground">({items.length} vente{items.length > 1 ? 's' : ''})</span>
              </div>
              <span className="font-bold" style={{ color: COLORS.success }}>+{formatCurrency(total)}</span>
            </button>
            
            {/* Détails - toujours visible pour le mois à déclarer, sinon selon état */}
            {(isToDeclarate || expandedMonths[month]) && (
              <div className={`${!isToDeclarate ? 'bg-muted/10' : ''}`}>
                {items.map((vente) => (
                  <div key={vente.id} className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-t border-dashed hover:bg-muted/20 transition-colors gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleEdit(vente)} className="p-1.5 hover:bg-muted rounded-lg"><Edit size={14} /></button>
                        <button onClick={() => handleDelete(vente.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-lg"><Trash2 size={14} /></button>
                      </div>
                      <div className={`min-w-0 ${!isToDeclarate ? 'text-sm text-muted-foreground' : ''}`}>
                        <span className="text-xs text-muted-foreground mr-2">{new Date(vente.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                        <span className={`font-medium ${!isToDeclarate ? 'text-muted-foreground' : ''}`}>{vente.client}</span>
                        {vente.description && <span className="text-muted-foreground ml-1 text-sm hidden sm:inline">({vente.description})</span>}
                        {/* Afficher la date d'encaissement si différente de la date de facturation */}
                        {vente.date_paiement && vente.date_paiement !== vente.date && (
                          <span className="text-xs text-emerald-600 ml-2">(encaissé le {new Date(vente.date_paiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 justify-end sm:justify-start ml-auto sm:ml-0">
                      {/* Bouton Encaisser pour les ventes en attente */}
                      {vente.statut === "en attente" && (
                        <button 
                          onClick={() => handleMarquerPaye(vente.id)}
                          disabled={actionLoading === vente.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                          title="Encaisser maintenant (date du jour)"
                        >
                          <Check size={12} />
                          {actionLoading === vente.id ? "..." : "Encaisser"}
                        </button>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${vente.statut === "payé" ? "bg-emerald-100 text-emerald-700" : vente.statut === "en attente" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {vente.statut === "payé" ? <Check size={10} /> : <Clock size={10} />}
                        {vente.statut}
                      </span>
                      {vente.deduire_frais && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{backgroundColor: COLORS.bg, color: COLORS.accent}}>
                          <CreditCard size={10} />
                        </span>
                      )}
                      <span className={`font-mono font-medium whitespace-nowrap ${!isToDeclarate ? 'text-sm text-muted-foreground' : ''}`} style={{ color: isToDeclarate ? COLORS.success : undefined }}>
                        +{formatCurrencyFull(vente.montant)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ventes-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{color: COLORS.text}}>MES VENTES</h1>
            <span className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold" style={{backgroundColor: COLORS.bg, color: COLORS.accent}}>{ventes.length}</span>
          </div>
          <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ date: new Date().toISOString().split('T')[0], client: "", description: "", montant: "", statut: "payé", activite_type: "principale", deduire_frais: false }); }}
            data-testid="add-vente-btn" className="flex items-center gap-2 px-4 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
            <Plus size={20} /> Nouvelle vente
          </button>
        </div>

        {/* Encart info frais plateforme si non configuré */}
        {!user?.frais_plateforme_pourcent && (
          <div className="rounded-xl p-4 border" style={{backgroundColor: COLORS.bg, borderColor: COLORS.accent + '40'}}>
            <div className="flex items-start gap-3">
              <Settings size={20} style={{color: COLORS.accent}} className="mt-0.5" />
              <div>
                <div className="font-medium" style={{color: COLORS.text}}>Tu utilises une plateforme de paiement ?</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure tes frais plateforme (Sumup, Stripe...) dans les paramètres pour suivre automatiquement tes dépenses liées aux commissions.
                </p>
                <Link to="/parametres" className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline" style={{color: COLORS.principale}}>
                  <Settings size={14} /> Configurer mes frais
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Ordre : En attente > CA à déclarer (période selon fréquence) > CA encaissé */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-xl border p-6" style={{borderLeft: `4px solid ${COLORS.warning}`}}>
            <div className="text-sm text-muted-foreground mb-1">En attente de paiement</div>
            <div className="text-2xl font-bold text-amber-600">{formatCurrency(enAttente)}</div>
          </div>
          <div className="bg-card rounded-xl border p-6" style={{borderLeft: `4px solid ${COLORS.accent}`}}>
            <div className="text-sm text-muted-foreground mb-1">CA à déclarer</div>
            <div className="text-2xl font-bold" style={{color: COLORS.text}}>{formatCurrency(caADeclarer)}</div>
            <div className="text-xs text-muted-foreground">{periodeADeclarer}</div>
          </div>
          <div className="bg-card rounded-xl border p-6" style={{borderLeft: `4px solid ${COLORS.success}`}}>
            <div className="text-sm text-muted-foreground mb-1">CA Encaissé (brut annuel)</div>
            <div className="text-2xl font-bold" style={{color: COLORS.success}}>{formatCurrency(totalVentes)}</div>
          </div>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 w-full max-w-md animate-slide-up max-h-[90vh] overflow-auto">
              <h2 className="text-xl font-bold mb-4" style={{color: COLORS.text}}>{editingId ? "Modifier la vente" : "Nouvelle vente"}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-sm font-medium mb-2">Date de facturation</label>
                  <input type="date" data-testid="vente-date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value, date_paiement: formData.date_paiement === formData.date ? e.target.value : formData.date_paiement })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" required />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium mb-2">Client</label>
                  <input type="text" data-testid="vente-client" value={formData.client}
                    onChange={(e) => { setFormData({ ...formData, client: e.target.value }); setShowClientSuggestions(true); }}
                    onFocus={() => setShowClientSuggestions(true)}
                    className="w-full h-11 px-4 rounded-lg border border-input bg-background" placeholder="Nom du client" required />
                  {showClientSuggestions && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {filteredClients.map((c, i) => (
                        <button key={i} type="button" onClick={() => { setFormData({ ...formData, client: c }); setShowClientSuggestions(false); }}
                          className="w-full text-left px-4 py-2 hover:bg-muted transition-colors">{c}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div><label className="block text-sm font-medium mb-2">Description <span className="text-muted-foreground font-normal">(facultatif)</span></label>
                  <input type="text" data-testid="vente-description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" placeholder="Nature de la prestation" />
                </div>
                
                {/* Montant HT pour assujettis, sinon montant brut */}
                <div><label className="block text-sm font-medium mb-2">{statutTva?.assujetti ? "Montant HT (€)" : "Montant (€)"}</label>
                  <input type="number" data-testid="vente-montant" value={formData.montant} onChange={(e) => handleMontantChange(e.target.value)} className="w-full h-11 px-4 rounded-lg border border-input bg-background" min="0" step="0.01" required />
                </div>
                
                {/* Section TVA pour assujettis */}
                {statutTva?.assujetti && (
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: COLORS.bg }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Info size={16} style={{ color: COLORS.accent }} />
                      <span className="font-medium text-sm" style={{ color: COLORS.accent }}>TVA collectée</span>
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-2">Taux de TVA</label>
                      <select 
                        value={formData.taux_tva} 
                        onChange={(e) => handleTauxTvaChange(e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-input bg-background"
                      >
                        {TAUX_TVA.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {formData.montant && (
                      <div className="grid grid-cols-2 gap-4 text-sm p-3 rounded" style={{ backgroundColor: COLORS.accent + '10' }}>
                        <div>
                          <span className="text-muted-foreground">Montant HT :</span>
                          <span className="ml-2 font-medium">{formatCurrency(parseFloat(formData.montant) || 0)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">TVA ({formData.taux_tva}%) :</span>
                          <span className="ml-2 font-medium" style={{ color: COLORS.warning }}>{formatCurrency(formData.montant_tva || 0)}</span>
                        </div>
                        <div className="col-span-2 pt-2 border-t">
                          <span className="text-muted-foreground">Montant TTC :</span>
                          <span className="ml-2 font-bold" style={{ color: COLORS.success }}>{formatCurrency(formData.montant_ttc || 0)}</span>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      ℹ️ La TVA collectée sera à reverser à l'État lors de ta déclaration de TVA.
                    </p>
                  </div>
                )}
                <div><label className="block text-sm font-medium mb-2">Statut</label>
                  <select 
                    data-testid="vente-statut" 
                    value={formData.statut} 
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })} 
                    className="w-full h-11 px-4 rounded-lg border border-input bg-background appearance-none"
                    style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                  >
                    <option value="payé">Payé</option><option value="en attente">En attente</option><option value="annulé">Annulé</option>
                  </select>
                </div>
                
                {/* Date d'encaissement - visible seulement si statut = payé */}
                {formData.statut === "payé" && (
                  <div className="p-4 rounded-lg border border-emerald-200" style={{backgroundColor: '#ecfdf5'}}>
                    <label className="block text-sm font-medium mb-2 text-emerald-700">
                      📅 Date d'encaissement
                      <span className="font-normal text-emerald-600 ml-1">(pour le calcul du CA à déclarer)</span>
                    </label>
                    <input 
                      type="date" 
                      data-testid="vente-date-paiement" 
                      value={formData.date_paiement || formData.date} 
                      onChange={(e) => setFormData({ ...formData, date_paiement: e.target.value })} 
                      className="w-full h-11 px-4 rounded-lg border border-emerald-300 bg-white" 
                    />
                    <p className="text-xs text-emerald-600 mt-2">
                      💡 Le CA sera comptabilisé sur le mois de l'encaissement ({new Date(formData.date_paiement || formData.date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })})
                    </p>
                  </div>
                )}
                
                {user?.double_activite && (
                  <div><label className="block text-sm font-medium mb-2">Activité</label>
                    <select 
                      data-testid="vente-activite" 
                      value={formData.activite_type} 
                      onChange={(e) => setFormData({ ...formData, activite_type: e.target.value })} 
                      className="w-full h-11 px-4 rounded-lg border border-input bg-background appearance-none"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      <option value="principale">Principale</option><option value="secondaire">Secondaire</option>
                    </select>
                  </div>
                )}
                
                {/* Case à cocher déduire frais */}
                {user?.frais_plateforme_pourcent > 0 && (
                  <div className="p-4 rounded-lg border" style={{backgroundColor: COLORS.bg + '40'}}>
                    <div className="flex items-center gap-3">
                      <input 
                        type="checkbox" 
                        id="deduire_frais" 
                        data-testid="vente-deduire-frais"
                        checked={formData.deduire_frais} 
                        onChange={(e) => setFormData({ ...formData, deduire_frais: e.target.checked })} 
                        className="w-5 h-5 rounded"
                      />
                      <label htmlFor="deduire_frais" className="text-sm font-medium cursor-pointer">
                        Enregistrer les frais plateforme ({user.frais_plateforme_pourcent}%) dans mes dépenses
                      </label>
                    </div>
                    {formData.deduire_frais && formData.montant && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <div>Montant facturé (CA à déclarer) : <strong>{formatCurrency(parseFloat(formData.montant) || 0)}</strong></div>
                        <div>Frais plateforme : <strong className="text-red-500">-{formatCurrency((parseFloat(formData.montant) || 0) * user.frais_plateforme_pourcent / 100)}</strong></div>
                        <div>Montant net reçu : <strong style={{color: COLORS.text}}>{formatCurrency((parseFloat(formData.montant) || 0) * (1 - user.frais_plateforme_pourcent / 100))}</strong></div>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      ℹ️ Les frais seront ajoutés dans "Mes Dépenses" pour ton suivi. <strong>Le CA déclaré à l'Urssaf reste le montant brut facturé</strong> (réglementation française).
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-lg border border-input hover:bg-muted transition-colors">Annuler</button>
                  <button type="submit" data-testid="vente-submit" className="flex-1 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">{editingId ? "Modifier" : "Ajouter"}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-card rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" /></div>
          ) : ventes.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground"><Receipt className="mx-auto mb-4 opacity-50" size={48} /><p>Aucune vente enregistrée</p></div>
          ) : (
            renderGroupedVentes()
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
